import { useState } from "react";
import {
  ShoppingCart, Receipt, Package, Warehouse, Truck, Users, BarChart3,
  CheckCircle2, ArrowRight, Menu, X, Star, Sparkles, ShieldCheck,
  Zap, Clock, TrendingUp, Globe2,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const NAV_LINKS = [
  { href: "#features", label: "Modules" },
  { href: "#workflow", label: "How it works" },
  { href: "#testimonials", label: "Customers" },
  { href: 'https://app.justerp.in/',label: "login"}
];

const MODULES = [
  { icon: ShoppingCart, title: "Purchase Management", desc: "Create, approve and track purchase orders end-to-end. Manage supplier catalogs, GRNs, lead times and payables in one place.", points: ["Multi-level PO approval", "GRN & invoice matching", "Supplier scorecards"] },
  { icon: Receipt, title: "Sales Management", desc: "Fast quote-to-cash for hotels, retail chains and cloud kitchens. Generate GST invoices and track collections in real time.", points: ["GST-ready invoices", "Route-wise order booking", "Auto payment reminders"] },
  { icon: Package, title: "Inventory Management", desc: "Every SKU, batch and expiry tracked with FEFO logic — built specifically for perishable food operations.", points: ["Batch & expiry tracking", "FEFO / FIFO picking", "Barcode & serial support"] },
  { icon: Warehouse, title: "Stock Management", desc: "Real-time stock across every warehouse, cold room and outlet. Automatic reorder points prevent stockouts and overstocking.", points: ["Multi-warehouse view", "Reorder point alerts", "Inter-branch transfers"] },
  { icon: Truck, title: "Supplier Directory", desc: "A living record of vendors — contracts, price lists, performance history and payment terms — all searchable.", points: ["Supplier price lists", "Compliance documents", "On-time performance"] },
  { icon: Users, title: "Customer Directory", desc: "Track buyers, chains and outlets with credit limits, price tiers and channel-wise sales analytics.", points: ["Credit limit checks", "Custom pricing tiers", "Channel analytics"] },
  { icon: BarChart3, title: "Reports & Analytics", desc: "Dashboards for revenue, margins, stock turnover and wastage — refreshed live from every transaction.", points: ["Gross margin analysis", "Wastage & expiry reports", "GST filing exports"] },
  { icon: ShieldCheck, title: "Compliance & Traceability", desc: "FSSAI-ready records with full batch traceability from farm to fork. Recall any batch in seconds if needed.", points: ["FSSAI batch traceability", "Audit trail on every action", "Role-based permissions"] },
];

// Pricing packages removed per client request.

const STATS_UNUSED_REMOVED = [];

const WORKFLOW = [
  { step: "01", title: "Procure", desc: "Raise POs to approved suppliers, track deliveries and match GRNs in one flow." },
  { step: "02", title: "Store", desc: "Receive stock with batch, expiry and QC data captured at the warehouse gate." },
  { step: "03", title: "Sell", desc: "Book orders from chains, restaurants and retail — auto-price by tier and channel." },
  { step: "04", title: "Deliver", desc: "Pick using FEFO logic, generate route sheets and confirm delivery on mobile." },
  { step: "05", title: "Report", desc: "Track margins, wastage, GST liability and turnover on live dashboards." },
];

const TESTIMONIALS = [
  { name: "Manav Chopra", role: "Founder, Blue Fig Bistro Group", quote: "We shut down three spreadsheets the week we onboarded ERP. Batch expiry alerts alone cut our wastage by 22% in the first quarter.", rating: 5 },
  { name: "Priya Nair", role: "COO, Coastal Dairy Co.", quote: "The multi-warehouse stock view is exactly what a perishables business needs. Our route managers now plan their day directly from ERP on their phones.", rating: 5 },
  { name: "Rohit Verma", role: "Director, Sunrise Bakery Supplies", quote: "GST filing used to be a three-day exercise. With ERP's reports it's now a two-hour review. Our accountant actually smiled last quarter.", rating: 5 },
];

const CLIENTS = ["GREEN VALLEY", "COASTAL DAIRY", "HIMALAYAN GRAINS", "FRESH CATCH", "SUNRISE BAKERY", "KONKAN SPICE", "URBAN GROCERS", "SKYLINE HOTELS", "BLUE FIG", "CAFE MOCHA"];

const FAQ = [
  { q: "Is ERP built specifically for food businesses?", a: "Yes. Every field, workflow and report is designed around perishables — batch codes, expiry tracking, cold-chain temperature logs, FSSAI compliance and food-specific units (kg, L, dozen, crates) are all first-class citizens." },
  { q: "Can I import data from Tally or my existing system?", a: "Absolutely. We provide CSV / Excel importers for masters (items, suppliers, customers, opening stock) and offer paid migration assistance where needed." },
  { q: "How is ERP different from a generic accounting tool?", a: "Generic tools treat inventory as boxes. ERP treats it as a living, expiring, temperature-sensitive product. FEFO picking, expiry alerts, wastage reports and cold-chain checks are built in — not bolted on." },
  { q: "Do you support GST invoicing and returns?", a: "Yes — GSTIN-based customer masters, HSN codes, IGST/CGST/SGST auto-split, e-invoice ready JSON exports and GSTR-1 / GSTR-3B summary reports are included." },
  { q: "How long does onboarding take?", a: "Most customers go live in 2-3 weeks including data migration, user training and warehouse setup. Timelines depend on the size and complexity of your operation." },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="landing-page">
      {/* NAVBAR */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-border" data-testid="navbar">
        <div className="container-x flex items-center justify-between h-16">
          <a href="#top" className="flex items-center gap-2.5" data-testid="brand-link">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-display font-bold text-sm"
              style={{ background: "linear-gradient(135deg, hsl(215, 100%, 40%), hsl(202, 90%, 45%))" }}
            >
              E
            </div>
            <div className="font-display font-bold text-base">ERP</div>
          </a>

          <nav
            className="hidden lg:flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50/80 px-3 py-2 shadow-sm backdrop-blur-sm"
            data-testid="desktop-nav"
          >
            <div className="flex items-center gap-2">
              {NAV_LINKS.filter((l) => l.label.toLowerCase() !== "login").map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-sm font-medium text-foreground/80 rounded-full px-3 py-1.5 transition-colors duration-200 hover:bg-white hover:text-blue-700"
                >
                  {l.label}
                </a>
              ))}
            </div>

            <a
              href={NAV_LINKS.find((l) => l.label.toLowerCase() === "login")?.href}
              data-testid="nav-link-login"
              className="ml-auto rounded-full bg-blue-600 px-4 py-1.5 text-md font-semibold text-white transition-colors duration-200 hover:bg-blue-700"
            >
              Login
            </a>
          </nav>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 rounded-md border border-border flex items-center justify-center"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-border bg-white" data-testid="mobile-nav">
            <div className="container-x py-4 flex flex-col gap-3">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-medium py-2"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(202, 100%, 55%, 0.18), transparent 70%)" }}
        />
        <div
          className="absolute top-20 -left-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(215, 100%, 60%, 0.16), transparent 70%)" }}
        />

        <div className="container-x relative pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 float-in">
            <div className="chip mb-6" data-testid="hero-chip">
              <Sparkles size={12} />
              Built for food distributors, hotels & cloud kitchens
            </div>
            <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl">
              The <span style={{ color: "hsl(215, 100%, 40%)" }}>food-first ERP</span>
              <br />
              that runs your entire supply chain.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              ERP brings purchase, sales, inventory and multi-warehouse stock together in one clean workspace — with batch expiry, FEFO picking and GST invoicing built in from day one.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> FSSAI & GST ready</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> Multi-warehouse support</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-600" /> Batch & expiry tracking</div>
            </div>
          </div>

          {/* Mock dashboard preview */}
          <div className="lg:col-span-5 relative" data-testid="hero-preview">
            <div
              className="relative rounded-2xl border border-border bg-white p-5 shadow-2xl"
              style={{ boxShadow: "0 40px 80px -30px rgba(0, 40, 120, 0.35)" }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Live Dashboard</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: "Sales MTD", v: "₹ 18.42 L", up: true, d: "+12.4%" },
                  { l: "Stock Value", v: "₹ 42.9 L", up: false, d: "-1.9%" },
                ].map((k) => (
                  <div key={k.l} className="rounded-lg border border-border p-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.l}</div>
                    <div className="font-display font-bold text-lg mt-1">{k.v}</div>
                    <div className={`text-[11px] font-medium mt-0.5 ${k.up ? "text-emerald-600" : "text-rose-600"}`}>{k.d}</div>
                  </div>
                ))}
              </div>

              <div className="mt-3 rounded-lg border border-border p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-semibold">Sales vs Purchases</div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "hsl(215, 100%, 40%)" }} /> Sales</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "hsl(202, 90%, 55%)" }} /> Purchases</span>
                  </div>
                </div>
                <svg viewBox="0 0 220 80" className="w-full h-20">
                  <polyline fill="none" stroke="hsl(215, 100%, 40%)" strokeWidth="2.5" points="5,55 40,45 75,48 110,32 145,28 180,18 215,10" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline fill="none" stroke="hsl(202, 90%, 55%)" strokeWidth="2.5" points="5,65 40,60 75,55 110,52 145,45 180,42 215,38" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <div className="mt-3 rounded-lg border border-border p-3 space-y-2">
                {[
                  { i: "Basmati Rice 25kg", w: "Mumbai", q: "+120", up: true },
                  { i: "Amul Butter 500g", w: "Delhi", q: "-48", up: false },
                  { i: "Chicken Breast 1kg", w: "Bengaluru", q: "-30", up: false },
                ].map((r) => (
                  <div key={r.i} className="flex items-center justify-between text-xs">
                    <div>
                      <div className="font-medium">{r.i}</div>
                      <div className="text-[10px] text-muted-foreground">{r.w}</div>
                    </div>
                    <span className={`font-mono-tab text-xs font-semibold ${r.up ? "text-emerald-600" : "text-rose-600"}`}>{r.q}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="absolute -bottom-6 -left-6 rounded-xl bg-white border border-border p-3 shadow-lg hidden md:flex items-center gap-3"
              style={{ boxShadow: "0 12px 24px -8px rgba(0, 40, 120, 0.15)" }}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "hsl(142, 70%, 92%)", color: "hsl(142, 70%, 30%)" }}>
                <CheckCircle2 size={16} />
              </div>
              <div>
                <div className="text-xs font-semibold">3 POs auto-approved</div>
                <div className="text-[10px] text-muted-foreground">2 mins ago</div>
              </div>
            </div>

            <div
              className="absolute -top-6 -right-6 rounded-xl bg-white border border-border p-3 shadow-lg hidden md:flex items-center gap-3"
              style={{ boxShadow: "0 12px 24px -8px rgba(0, 40, 120, 0.15)" }}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "hsl(38, 100%, 92%)", color: "hsl(38, 90%, 32%)" }}>
                <Clock size={16} />
              </div>
              <div>
                <div className="text-xs font-semibold">3 batches expire in 7 days</div>
                <div className="text-[10px] text-muted-foreground">FEFO plan ready</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <section className="border-y border-border bg-secondary/40 py-8 overflow-hidden" data-testid="clients-strip">
        <div className="container-x">
          <div className="text-center text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-6">
            Trusted by food businesses across India
          </div>
          <div className="overflow-hidden relative">
            <div className="marquee">
              {[...CLIENTS, ...CLIENTS].map((c, i) => (
                <div key={i} className="font-display font-bold text-lg tracking-wider text-muted-foreground/70">
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MODULES / FEATURES */}
      <section id="features" className="py-24 lg:py-32" data-testid="modules-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="section-eyebrow">Modules</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-3">
              Every piece of your food business,
              <br />
              <span style={{ color: "hsl(215, 100%, 40%)" }}>connected in one system.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Stop juggling spreadsheets, WhatsApp orders and separate accounting software. ERP unifies procurement, sales, inventory and stock into a single workspace built for the way food actually moves.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="modules-grid">
            {MODULES.map((m) => (
              <div key={m.title} className="feature-card" data-testid={`module-${m.title.toLowerCase().replace(/\s|&/g, "-")}`}>
                <div className="feature-icon">
                  <m.icon size={20} strokeWidth={2} />
                </div>
                <h3 className="font-display font-bold text-lg mt-5">{m.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
                <ul className="mt-5 space-y-2">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-xs text-foreground/80">
                      <CheckCircle2 size={14} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="py-24 lg:py-32 bg-secondary/40" data-testid="workflow-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="section-eyebrow">The workflow</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-3">
              Procure. Store. Sell. Deliver. Report.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Five stages of your food supply chain — one clean workflow. Every action is tracked, timestamped and traceable back to the person, batch and location.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="workflow-steps">
            {WORKFLOW.map((w, i) => (
              <div key={w.step} className="relative" data-testid={`workflow-${w.step}`}>
                <div className="bg-white border border-border rounded-xl p-6 h-full">
                  <div className="font-mono-tab text-xs font-semibold text-primary tracking-wider">STEP {w.step}</div>
                  <h3 className="font-display font-bold text-lg mt-2">{w.title}</h3>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{w.desc}</p>
                </div>
                {i < WORKFLOW.length - 1 && (
                  <ArrowRight size={16} className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 text-primary/60" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFIT SPLIT */}
      <section className="py-24 lg:py-32" data-testid="benefits-section">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div className="max-w-2xl">
            <div className="section-eyebrow">Why food-first?</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-3">
              Because food isn&rsquo;t just inventory —<br />
              <span style={{ color: "hsl(215, 100%, 40%)" }}>it&rsquo;s alive, perishable and regulated.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Generic ERPs give you rows and columns. ERP gives you the batch that expires on Tuesday, the cold room that hit 12°C last night, and the customer whose credit limit is 90% used. It's ERP that thinks like a food operator.
            </p>

            <div className="mt-10 space-y-6">
              {[
                { icon: Zap, title: "Real-time FEFO picking", desc: "Every pick list is auto-generated using First Expiry First Out to minimise wastage." },
                { icon: Clock, title: "Expiry & wastage alerts", desc: "Get notified 7, 30 and 90 days before batches expire — and act before write-offs." },
                { icon: TrendingUp, title: "Margin visibility per SKU", desc: "See your true gross margin per item, per customer, per outlet — updated after every invoice." },
                { icon: Globe2, title: "Cloud-first, mobile-ready", desc: "Warehouse staff work on phones, managers work on laptops, owners work from anywhere." },
              ].map((b) => (
                <div key={b.title} className="flex gap-4" data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="feature-icon flex-shrink-0"><b.icon size={20} /></div>
                  <div>
                    <h3 className="font-display font-bold text-base">{b.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div
              className="rounded-2xl overflow-hidden border border-border relative"
              style={{ boxShadow: "0 40px 80px -30px rgba(0, 40, 120, 0.35)" }}
            >
              <img
                src="https://images.unsplash.com/photo-1612362426802-dcc0ccd25f64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxmcmVzaCUyMG9yZ2FuaWMlMjBmb29kJTIwaW5ncmVkaWVudHMlMjBmbGF0bGF5fGVufDB8fHx8MTc4NTE0MTQ0NXww&ixlib=rb-4.1.0&q=85"
                alt="Fresh food ingredients"
                className="w-full h-[440px] object-cover"
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(180deg, transparent 50%, hsl(215, 60%, 10%, 0.55) 100%)" }}
              />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-xs uppercase tracking-widest font-semibold text-white/80">Perishable-ready</div>
                <div className="font-display font-bold text-2xl mt-1 leading-tight">From farm to fork, every batch is accountable.</div>
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border border-border p-4 hidden md:block shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "hsl(142, 70%, 92%)", color: "hsl(142, 70%, 30%)" }}>
                  <TrendingUp size={18} />
                </div>
                <div>
                  <div className="font-display font-bold text-sm">-22% wastage</div>
                  <div className="text-[11px] text-muted-foreground">Avg. across customers in year 1</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 lg:py-32 bg-secondary/40" data-testid="testimonials-section">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="section-eyebrow">Loved by operators</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-3">
              Food businesses that rely on ERP every day.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="feature-card" data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-4 text-base leading-relaxed text-foreground/90">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-border">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm"
                    style={{ background: "hsl(210, 100%, 94%)", color: "hsl(215, 100%, 32%)" }}
                  >
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-secondary/40" data-testid="faq-section">
        <div className="container-x grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="section-eyebrow">FAQ</div>
            <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-3">Questions we get from food business owners.</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Can&rsquo;t find what you&rsquo;re looking for? <a href="#contact" className="text-primary font-semibold underline underline-offset-2">Talk to our team</a> — we usually reply within a business hour.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-border rounded-xl px-5 [&>*]:border-0" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display font-semibold text-base hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="navy-section pt-16 pb-8" data-testid="footer">
        <div className="container-x">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-display font-bold text-sm"
                  style={{ background: "linear-gradient(135deg, hsl(215, 100%, 45%), hsl(202, 90%, 50%))" }}
                >
                  E
                </div>
                <div className="font-display font-bold text-white text-base">ERP</div>
              </div>
              <p className="text-sm text-white/70 mt-5 max-w-xs leading-relaxed">
                The food-first ERP that runs your entire supply chain — from procurement to plate.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Product</div>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="#features" className="hover:text-white">Modules</a></li>
                <li><a href="#workflow" className="hover:text-white">Workflow</a></li>
                <li><a href="#testimonials" className="hover:text-white">Customers</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Company</div>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="#" className="hover:text-white">About us</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Press kit</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Resources</div>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="#" className="hover:text-white">Help center</a></li>
                <li><a href="#" className="hover:text-white">API docs</a></li>
                <li><a href="#" className="hover:text-white">GST guide</a></li>
                <li><a href="#" className="hover:text-white">Status</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs text-white/60">© 2026 ERP Pvt. Ltd. All rights reserved.</div>
            <div className="flex items-center gap-6 text-xs text-white/60">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
              <a href="#" className="hover:text-white">Security</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
